/**
 * shop-frontend/src/utils/productImage.js
 *
 * Safe image picker for product cards and product details.
 * Always returns a clean URL string (never objects, JSON, or mutated URLs).
 *
 * Usage:
 *   import { getDisplayImage, getProductImages } from '../utils/productImage';
 *   <img src={toAbsUrl(getDisplayImage(product, selectedVariantId))} />
 */

/**
 * Pick the single best display image for a product card or thumbnail.
 *
 * Priority:
 *  1. Selected variant's first image (if variantId provided and variant has images)
 *  2. Product's first image from images[]
 *  3. Legacy fallback fields (image, imageUrl, media)
 *
 * @param {Object} product - product object from API
 * @param {string} [selectedVariantId] - if user selected a variant
 * @returns {string} image URL (may still need toAbsUrl wrapping)
 */
export function getDisplayImage(product, selectedVariantId) {
  if (!product) return "";

  // Selected variant images take priority
  if (selectedVariantId) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const v = variants.find((x) => String(x._id || x.id || "") === String(selectedVariantId));
    const vImages = v && Array.isArray(v.images) ? v.images.filter(Boolean) : [];
    if (vImages[0]) return pickStr(vImages[0]);
  }

  // Product-level images
  const images = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  if (images[0]) return pickStr(images[0]);

  // Legacy fallbacks
  if (product.image) return pickStr(product.image);
  if (product.imageUrl) return pickStr(product.imageUrl);

  if (Array.isArray(product.displayImages) && product.displayImages[0]) {
    return pickStr(product.displayImages[0]);
  }

  const media = product.media;
  if (media && typeof media === "object") {
    if (Array.isArray(media.images) && media.images[0]) return pickStr(media.images[0]);
    if (media.image) return pickStr(media.image);
  }

  // ✅ FIX: Fall back to first variant's first image
  const variants = Array.isArray(product.variants) ? product.variants : [];
  for (const v of variants) {
    const vImages = v && Array.isArray(v.images) ? v.images.filter(Boolean) : [];
    if (vImages[0]) return pickStr(vImages[0]);
  }

  return "";
}

/**
 * Get all display images for a product gallery.
 * If a variant is selected, show variant images first, then base images.
 *
 * @param {Object} product - product object from API
 * @param {string} [selectedVariantId] - if user selected a variant
 * @returns {string[]} array of image URLs
 */
export function getProductImages(product, selectedVariantId) {
  if (!product) return [];

  const baseImages = (Array.isArray(product.images) ? product.images : [])
    .map(pickStr)
    .filter(Boolean);

  if (selectedVariantId) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const v = variants.find((x) => String(x._id || x.id || "") === String(selectedVariantId));
    const vImages = v && Array.isArray(v.images) ? v.images : [];
    const cleanVImages = vImages.map(pickStr).filter(Boolean);

    if (cleanVImages.length) {
      // Variant images first, then unique base images not already in variant set
      const seen = new Set(cleanVImages);
      const extra = baseImages.filter((x) => !seen.has(x));
      return [...cleanVImages, ...extra];
    }
  }

  return baseImages.length ? baseImages : [getDisplayImage(product)].filter(Boolean);
}

/**
 * Extract a string URL from any value (handles objects, JSON strings).
 */
function pickStr(val) {
  if (!val) return "";
  if (typeof val === "string") {
    const s = val.trim();
    if (!s || s === "[object Object]") return "";
    // Try JSON parse for wrapped values
    if (s.startsWith("{") || s.startsWith("[")) {
      try {
        return pickStr(JSON.parse(s));
      } catch {
        return "";
      }
    }
    return s.replace(/^['"]+|['"]+$/g, "").trim();
  }
  if (Array.isArray(val)) {
    for (const item of val) {
      const out = pickStr(item);
      if (out) return out;
    }
    return "";
  }
  if (typeof val === "object") {
    const direct = pickStr(val.url || val.secure_url || val.path || val.image || val.src || "");
    if (direct) return direct;

    const publicId = pickStr(val.public_id || "");
    if (publicId) {
      const resourceType = pickStr(val.resource_type || "image") || "image";
      const format = pickStr(val.format || "");
      const withExt = format && !/\.[a-z0-9]{2,6}$/i.test(publicId)
        ? `${publicId}.${format}`
        : publicId;
      return `https://res.cloudinary.com/dohetomaw/${resourceType}/upload/${withExt}`;
    }
    return "";
  }
  return "";
}
